import { TFunction } from 'i18next';

import { FormTeamService } from './Form/types';
import { CostCalculation, CostCalculationEntry } from 'entities';

export const operations = {
  SUM: {
    id: 'SUM',
    getLabel: (t: TFunction) => t('sum'),
    sign: '+',
    hasValue: true,
  },

  SUBTRACT: {
    id: 'SUBTRACT',
    getLabel: (t: TFunction) => t('subtract'),
    sign: '-',
    hasValue: true,
  },

  MULTIPLY: {
    id: 'MULTIPLY',
    getLabel: (t: TFunction) => t('multiply'),
    sign: '*',
    hasValue: true,
  },

  DIVIDE: {
    id: 'DIVIDE',
    getLabel: (t: TFunction) => t('divide'),
    sign: '/',
    hasValue: true,
  },

  AS_TOTAL: {
    id: 'AS_TOTAL',
    getLabel: (t: TFunction) => t('as-total'),
    sign: null,
    hasValue: false,
  },
};

function resolveSingleCalculation(calculation: CostCalculation, service: FormTeamService): string {
  function resolveCalc(entry: CostCalculationEntry, service: FormTeamService): string {
    const { costVariables, costCalculation } = service;

    if (entry.type === 'VARIABLE')
      return `${costVariables.find(({ id }) => id === entry.ref)?.name || '#ERROR'}`;

    const calculationReferenced = costCalculation.find(({ id }) => id === entry.ref);

    if (!calculationReferenced) return '#ERROR';

    return resolveSingleCalculation(calculationReferenced, service);
  }

  const { base, operation, value } = calculation;

  const { hasValue, sign } = operations[operation];

  const baseRef = resolveCalc(base, service);

  if (!sign) return `(${baseRef})`;

  return `(${baseRef} ${sign}${
    hasValue ? ` ${resolveCalc(value as CostCalculationEntry, service)}` : ''
  })`;
}

export function resolveReferences(
  reference: CostCalculationEntry,
  service: FormTeamService,
): FormTeamService['costVariables'] {
  const { type, ref } = reference;

  if (type === 'VARIABLE') return service.costVariables.filter(({ id }) => id === ref);

  const calcRef = service.costCalculation.find(({ id }) => id === ref);

  if (!calcRef) return [];

  const { base, value } = calcRef;

  const toResolve = [base, value].filter(Boolean) as CostCalculationEntry[];

  return toResolve.map((entry) => resolveReferences(entry, service).flat()).flat();
}

export function validadeCurrencyRef(
  { base, value }: CostCalculation,
  service: FormTeamService,
): boolean {
  const refs = [base, value].filter(Boolean) as CostCalculationEntry[];

  const resolved = refs.map((ref) => resolveReferences(ref, service)).flat();

  return !!resolved.find(({ type }) => type === 'CURRENCY');
}

export function getLastCurrencyReference(service: FormTeamService): CostCalculation | undefined {
  const lastCurrencyRef = service.costCalculation
    .slice()
    .reverse()
    .find((calculation) => validadeCurrencyRef(calculation, service));

  return lastCurrencyRef;
}

export function getCalculationReference(service: FormTeamService): CostCalculation | undefined {
  const asTotalCalc = service.costCalculation.find(({ operation }) => operation === 'AS_TOTAL');

  const calculationRef = asTotalCalc || getLastCurrencyReference(service);

  return calculationRef;
}

export function createExpression(service: FormTeamService): string {
  const calculationRef = getCalculationReference(service);

  if (!calculationRef) return '#NULL';

  return resolveSingleCalculation(calculationRef, service);
}
